import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ETIQUETA_GENETICA,
  creditos,
  formatearCantidad,
  sufijoUnidad,
} from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  Vacio,
} from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Categoria {
  id: string;
  nombre: string;
  color: string;
  unidadMedida: string;
  orden: number;
  activa: boolean;
  _count: { productos: number };
}

interface Producto {
  id: string;
  nombre: string;
  descripcion: string | null;
  genetica: string;
  thc: number | null;
  cbd: number | null;
  precioCentimos: number;
  stockMg: number;
  stockMinMg: number;
  activo: boolean;
  categoria: {
    id: string;
    nombre: string;
    color: string;
    unidadMedida: string;
  };
  marca: { id: string; nombre: string } | null;
  precios: { tipoSocioId: string; precioCentimos: number }[];
}

interface Marca {
  id: string;
  nombre: string;
  activa: boolean;
  _count?: { productos: number };
}

interface TipoSocio {
  id: string;
  nombre: string;
  color: string;
  descuentoPorcentaje: number;
}

const FORMULARIO_VACIO = {
  categoriaId: "",
  marcaId: "",
  nombre: "",
  descripcion: "",
  genetica: "NA",
  thc: "",
  cbd: "",
  precioEuros: "",
  stockMinG: "0",
  activo: true,
};

export default function Catalogo() {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [editando, setEditando] = useState<Producto | "nuevo" | null>(null);
  const [nuevaCategoria, setNuevaCategoria] = useState(false);
  const [verMarcas, setVerMarcas] = useState(false);
  const [error, setError] = useState("");

  const categorias = useQuery({
    queryKey: ["categorias"],
    queryFn: () => api<Categoria[]>("/catalogo/categorias"),
  });
  const productos = useQuery({
    queryKey: ["productos"],
    queryFn: () => api<Producto[]>("/catalogo/productos"),
  });
  const tipos = useQuery({
    queryKey: ["tipos-socio"],
    queryFn: () => api<TipoSocio[]>("/tipos-socio"),
  });
  const marcas = useQuery({
    queryKey: ["marcas"],
    queryFn: () => api<Marca[]>("/catalogo/marcas"),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["productos"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["categorias"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["marcas"] });
    void clienteConsultas.invalidateQueries({
      queryKey: ["productos-mostrador"],
    });
  };

  return (
    <>
      <Cabecera
        titulo={t("ct.titulo")}
        descripcion={t("ct.descripcion")}
      >
        {puede("importarProductos") && (
          <Link className="boton secundario" to="/catalogo/importar">
            {t("ct.importar")}
          </Link>
        )}
        {puede("gestionarCatalogo") && (
          <>
            <button
              className="boton secundario"
              onClick={() => setVerMarcas(true)}
            >
              {t("ct.marcas")}
            </button>
            <button
              className="boton secundario"
              onClick={() => setNuevaCategoria(true)}
            >
              {t("ct.nuevaCategoria")}
            </button>
            <button className="boton" onClick={() => setEditando("nuevo")}>
              {t("ct.nuevoProducto")}
            </button>
          </>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {categorias.data && categorias.data.length > 0 && (
        <div className="fila ajustable" style={{ marginBottom: 16 }}>
          {categorias.data.map((c) => (
            <span
              key={c.id}
              className="etiqueta"
              style={{ background: `${c.color}20`, color: c.color }}
            >
              {c.nombre} · {c._count.productos}
            </span>
          ))}
        </div>
      )}

      <Panel sinCuerpo>
        {productos.isLoading ? (
          <Cargando />
        ) : !productos.data || productos.data.length === 0 ? (
          <Vacio>{t("ct.sinProductos")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("ct.producto")}</th>
                  <th>{t("ct.categoria")}</th>
                  <th>{t("ct.genetica")}</th>
                  <th className="num">{t("ct.thcCbd")}</th>
                  <th className="num">{t("ct.precio")}</th>
                  <th className="num">{t("ct.stock")}</th>
                  <th>{t("ct.estado")}</th>
                  {puede("gestionarCatalogo") && <th />}
                </tr>
              </thead>
              <tbody>
                {productos.data.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/catalogo/${p.id}`}>
                        <strong>{p.nombre}</strong>
                      </Link>
                      {p.marca && (
                        <div className="suave pequeno">{p.marca.nombre}</div>
                      )}
                      {p.descripcion && (
                        <div className="suave pequeno">{p.descripcion}</div>
                      )}
                    </td>
                    <td>
                      <span
                        className="etiqueta"
                        style={{
                          background: `${p.categoria.color}20`,
                          color: p.categoria.color,
                        }}
                      >
                        {p.categoria.nombre}
                      </span>
                    </td>
                    <td className="pequeno">
                      {ETIQUETA_GENETICA[p.genetica] ?? p.genetica}
                    </td>
                    <td className="num pequeno tabular">
                      {p.thc ? `${p.thc}%` : "—"} / {p.cbd ? `${p.cbd}%` : "—"}
                    </td>
                    <td className="num tabular">
                      {creditos(p.precioCentimos)}
                      <span className="suave pequeno">
                        /{sufijoUnidad(p.categoria.unidadMedida)}
                      </span>
                    </td>
                    <td className="num tabular">
                      {formatearCantidad(p.stockMg, p.categoria.unidadMedida)}
                      {p.stockMinMg > 0 && p.stockMg <= p.stockMinMg && (
                        <div>
                          <Etiqueta tipo="roja">{t("ct.bajoMinimo")}</Etiqueta>
                        </div>
                      )}
                    </td>
                    <td>
                      {p.activo ? (
                        <Etiqueta tipo="verde">{t("ct.activo")}</Etiqueta>
                      ) : (
                        <Etiqueta>{t("ct.inactivo")}</Etiqueta>
                      )}
                    </td>
                    {puede("gestionarCatalogo") && (
                      <td>
                        <button
                          className="boton fantasma"
                          onClick={() => setEditando(p)}
                        >
                          {t("ct.editar")}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {editando && categorias.data && (
        <DialogoProducto
          producto={editando === "nuevo" ? null : editando}
          categorias={categorias.data}
          marcas={marcas.data ?? []}
          tipos={tipos.data ?? []}
          alCerrar={() => setEditando(null)}
          alFallar={setError}
          alHecho={() => {
            setEditando(null);
            refrescar();
          }}
        />
      )}

      {verMarcas && (
        <DialogoMarcas
          alCerrar={() => setVerMarcas(false)}
          alCambiar={refrescar}
        />
      )}

      {nuevaCategoria && (
        <DialogoCategoria
          alCerrar={() => setNuevaCategoria(false)}
          alFallar={setError}
          alHecho={() => {
            setNuevaCategoria(false);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoProducto({
  producto,
  categorias,
  marcas,
  tipos,
  alCerrar,
  alHecho,
  alFallar,
}: {
  producto: Producto | null;
  categorias: Categoria[];
  marcas: Marca[];
  tipos: TipoSocio[];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [datos, setDatos] = useState(
    producto
      ? {
          categoriaId: producto.categoria.id,
          marcaId: producto.marca?.id ?? "",
          nombre: producto.nombre,
          descripcion: producto.descripcion ?? "",
          genetica: producto.genetica,
          thc: producto.thc?.toString() ?? "",
          cbd: producto.cbd?.toString() ?? "",
          precioEuros: (producto.precioCentimos / 100).toString(),
          stockMinG: (producto.stockMinMg / 1000).toString(),
          activo: producto.activo,
        }
      : { ...FORMULARIO_VACIO, categoriaId: categorias[0]?.id ?? "" },
  );
  const [enviando, setEnviando] = useState(false);

  const cambiar = (campo: string, valor: string | boolean) =>
    setDatos((d) => ({ ...d, [campo]: valor }));

  // Precio fijo por tipo de socio; vacío = se aplica el descuento general del tipo
  const [precios, setPrecios] = useState<Record<string, string>>(
    Object.fromEntries(
      (producto?.precios ?? []).map((p) => [
        p.tipoSocioId,
        String(p.precioCentimos / 100),
      ]),
    ),
  );

  async function guardar() {
    setEnviando(true);
    try {
      const cuerpo = {
        categoriaId: datos.categoriaId,
        marcaId: datos.marcaId || null,
        nombre: datos.nombre,
        descripcion: datos.descripcion || null,
        genetica: datos.genetica,
        thc: datos.thc ? Number(datos.thc) : null,
        cbd: datos.cbd ? Number(datos.cbd) : null,
        precioEuros: Number(datos.precioEuros || 0),
        stockMinG: Number(datos.stockMinG || 0),
        activo: datos.activo,
      };
      const guardado = producto
        ? await api<{ id: string }>(`/catalogo/productos/${producto.id}`, {
            metodo: "PATCH",
            cuerpo,
          })
        : await api<{ id: string }>("/catalogo/productos", {
            metodo: "POST",
            cuerpo,
          });
      if (tipos.length > 0) {
        await api(`/catalogo/productos/${guardado.id}/precios`, {
          metodo: "PUT",
          cuerpo: tipos.map((t) => ({
            tipoSocioId: t.id,
            precioEuros: precios[t.id] ? Number(precios[t.id]) : null,
          })),
        });
      }
      alHecho();
    } catch (fallo) {
      alFallar(
        fallo instanceof Error
          ? fallo.message
          : t("ct.p.fallo"),
      );
      alCerrar();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={producto ? t("ct.p.editar", { nombre: producto.nombre }) : t("ct.p.nuevo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            onClick={guardar}
            disabled={enviando || !datos.nombre || !datos.categoriaId}
          >
            {enviando ? t("g.guardando") : t("g.guardar")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("ct.p.nombre")}>
        <input
          value={datos.nombre}
          onChange={(e) => cambiar("nombre", e.target.value)}
          autoFocus
        />
      </Campo>

      <div className="linea-campos">
        <Campo etiqueta={t("ct.p.categoria")}>
          <select
            value={datos.categoriaId}
            onChange={(e) => cambiar("categoriaId", e.target.value)}
          >
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          etiqueta={t("ct.p.marca")}
          ayuda={marcas.length === 0 ? t("ct.p.sinMarcasAun") : undefined}
        >
          <select
            value={datos.marcaId}
            onChange={(e) => cambiar("marcaId", e.target.value)}
          >
            <option value="">{t("ct.p.sinMarca")}</option>
            {marcas
              .filter((m) => m.activa || m.id === datos.marcaId)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
          </select>
        </Campo>

        <Campo etiqueta={t("ct.p.genetica")}>
          <select
            value={datos.genetica}
            onChange={(e) => cambiar("genetica", e.target.value)}
          >
            <option value="NA">{t("ct.p.sinEspecificar")}</option>
            {["SATIVA", "INDICA", "HIBRIDA", "CBD"].map((g) => (
              <option key={g} value={g}>
                {t(`genetica.${g}` as Clave)}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <div className="linea-campos">
        <Campo etiqueta={t("ct.p.thc")}>
          <input
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={datos.thc}
            onChange={(e) => cambiar("thc", e.target.value)}
          />
        </Campo>
        <Campo etiqueta={t("ct.p.cbd")}>
          <input
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={datos.cbd}
            onChange={(e) => cambiar("cbd", e.target.value)}
          />
        </Campo>
      </div>

      <div className="linea-campos">
        <Campo
          etiqueta={
            sufijoUnidad(categorias.find((c) => c.id === datos.categoriaId)?.unidadMedida) === "ud"
              ? t("ct.p.aportacionUnidad")
              : t("ct.p.aportacionGramo")
          }
        >
          <input
            type="number"
            step="0.01"
            min={0}
            value={datos.precioEuros}
            onChange={(e) => cambiar("precioEuros", e.target.value)}
          />
        </Campo>
        <Campo
          etiqueta={t("ct.p.stockMinimo", {
            unidad: sufijoUnidad(categorias.find((c) => c.id === datos.categoriaId)?.unidadMedida),
          })}
          ayuda={t("ct.p.stockMinimoAyuda")}
        >
          <input
            type="number"
            step="1"
            min={0}
            value={datos.stockMinG}
            onChange={(e) => cambiar("stockMinG", e.target.value)}
          />
        </Campo>
      </div>

      {tipos.length > 0 && (
        <div style={{ margin: "4px 0 14px" }}>
          <div className="rotulo" style={{ marginBottom: 6 }}>
            {t("ct.p.precioPorTipo")}
          </div>
          <div className="linea-campos">
            {tipos.map((tipo) => (
              <Campo
                key={tipo.id}
                etiqueta={tipo.nombre}
                ayuda={
                  precios[tipo.id]
                    ? t("ct.p.sustituye")
                    : tipo.descuentoPorcentaje > 0
                      ? t("ct.p.vacioConDescuento", { porcentaje: tipo.descuentoPorcentaje })
                      : t("ct.p.vacioCatalogo")
                }
              >
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  placeholder="—"
                  value={precios[tipo.id] ?? ""}
                  onChange={(e) =>
                    setPrecios((p) => ({ ...p, [tipo.id]: e.target.value }))
                  }
                />
              </Campo>
            ))}
          </div>
        </div>
      )}

      <Campo etiqueta={t("ct.p.descripcion")}>
        <textarea
          rows={2}
          value={datos.descripcion}
          onChange={(e) => cambiar("descripcion", e.target.value)}
        />
      </Campo>

      <label className="casilla">
        <input
          type="checkbox"
          checked={datos.activo}
          onChange={(e) => cambiar("activo", e.target.checked)}
        />
        <span>{t("ct.p.disponible")}</span>
      </label>
    </Modal>
  );
}

function DialogoCategoria({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [nombre, setNombre] = useState("");
  const [color, setColor] = useState("#4b8b3b");
  const [unidadMedida, setUnidadMedida] = useState("PESO");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("ct.c.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !nombre}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/catalogo/categorias", {
                  metodo: "POST",
                  cuerpo: { nombre, color, unidadMedida },
                });
                alHecho();
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error
                    ? fallo.message
                    : t("ct.c.fallo"),
                );
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("ct.c.crear")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("ct.c.nombre")}>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          autoFocus
        />
      </Campo>
      <div className="linea-campos">
        <Campo etiqueta={t("ct.c.color")}>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ height: 42 }}
          />
        </Campo>
        <Campo etiqueta={t("ct.c.seDispensaPor")}>
          <select
            value={unidadMedida}
            onChange={(e) => setUnidadMedida(e.target.value)}
          >
            <option value="PESO">{t("ct.c.peso")}</option>
            <option value="UNIDAD">{t("ct.c.unidades")}</option>
          </select>
        </Campo>
      </div>
    </Modal>
  );
}

/**
 * Las marcas del genero. Las pone cada club: quien trabaja con bancos conocidos
 * los quiere ver en la ficha, y quien cultiva lo suyo deja la lista vacia.
 *
 * Borrar una marca deja sin marca a sus productos, no los borra: una marca es
 * una etiqueta, y quitarla no puede llevarse por delante el genero.
 */
function DialogoMarcas({
  alCerrar,
  alCambiar,
}: {
  alCerrar: () => void;
  alCambiar: () => void;
}) {
  const { t, cuenta } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const lista = useQuery({
    queryKey: ["marcas"],
    queryFn: () => api<Marca[]>("/catalogo/marcas"),
  });

  async function hacer(accion: () => Promise<unknown>) {
    setError("");
    try {
      await accion();
      void clienteConsultas.invalidateQueries({ queryKey: ["marcas"] });
      alCambiar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ct.m.fallo"));
    }
  }

  async function anadir() {
    setEnviando(true);
    await hacer(async () => {
      await api("/catalogo/marcas", { metodo: "POST", cuerpo: { nombre } });
      setNombre("");
    });
    setEnviando(false);
  }

  return (
    <Modal
      titulo={t("ct.m.titulo")}
      alCerrar={alCerrar}
      acciones={
        <button className="boton" onClick={alCerrar}>
          {t("ct.m.listo")}
        </button>
      }
    >
      <Aviso tipo="error">{error}</Aviso>

      <div className="fila" style={{ marginBottom: 14 }}>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t("ct.m.ejemplo")}
          maxLength={60}
          onKeyDown={(e) =>
            e.key === "Enter" && nombre.trim().length >= 2 && void anadir()
          }
        />
        <button
          className="boton"
          disabled={enviando || nombre.trim().length < 2}
          onClick={() => void anadir()}
        >
          {t("ct.m.anadir")}
        </button>
      </div>

      {lista.isLoading ? (
        <Cargando />
      ) : (lista.data ?? []).length === 0 ? (
        <Vacio>{t("ct.m.sinMarcas")}</Vacio>
      ) : (
        <ul className="lista-categorias">
          {(lista.data ?? []).map((m) => (
            <li key={m.id} className={m.activa ? undefined : "apagada"}>
              <div>
                <strong>{m.nombre}</strong>
                <div className="suave pequeno">
                  {m.activa
                    ? cuenta(m._count?.productos ?? 0, "ct.m.unProducto", "ct.m.productos")
                    : t("ct.m.apagada")}
                </div>
              </div>
              <div className="fila" style={{ gap: 2 }}>
                <button
                  className="boton fantasma"
                  onClick={() =>
                    void hacer(() =>
                      api(`/catalogo/marcas/${m.id}`, {
                        metodo: "PATCH",
                        cuerpo: { activa: !m.activa },
                      }),
                    )
                  }
                >
                  {m.activa ? t("ct.m.apagar") : t("ct.m.encender")}
                </button>
                <button
                  className="boton fantasma"
                  onClick={() => {
                    const cuantos = m._count?.productos ?? 0;
                    const aviso = cuantos
                      ? t("ct.m.confirmarConProductos", {
                          productos: cuenta(cuantos, "ct.m.unProducto", "ct.m.productos"),
                        })
                      : t("ct.m.confirmarBorrar", { nombre: m.nombre });
                    if (!window.confirm(aviso)) return;
                    void hacer(() =>
                      api(`/catalogo/marcas/${m.id}`, { metodo: "DELETE" }),
                    );
                  }}
                >
                  {t("ct.m.borrar")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        {t("ct.m.nota")}
      </p>
    </Modal>
  );
}
